import { Component } from 'react';

/**
 * Top-level error boundary (audit §10 #10 / docs/37 P1).
 *
 * Without this, one thrown render error white-screens the entire SPA. This
 * catches render/lifecycle errors below it and shows a recoverable fallback
 * instead of a blank page. It deliberately uses only inline styles + theme
 * tokens so the fallback still renders even if a stylesheet or provider is
 * the thing that broke.
 *
 * Note: React error boundaries catch errors during render, in lifecycles, and
 * in constructors of the tree below — NOT event handlers, async code, or the
 * boundary itself. Those paths still need their own try/catch.
 */
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    // Surface for local debugging + any error tracker wired onto window.
    // Never throws: a broken reporter must not re-crash the fallback.
    try {
      console.error('Uncaught render error:', error, info?.componentStack);
      if (window.Sentry?.captureException) {
        window.Sentry.captureException(error, { extra: { componentStack: info?.componentStack } });
      }
    } catch { /* reporting is best-effort */ }
  }

  render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <div style={{
        minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '24px', textAlign: 'center',
        background: 'var(--bg-page, #f8f9fb)', color: 'var(--text-primary, #1a1a1a)',
        fontFamily: 'Inter, system-ui, sans-serif',
      }}>
        <div style={{ maxWidth: '420px' }}>
          <div style={{ fontSize: '40px', marginBottom: '12px' }} role="img" aria-label="warning">⚠️</div>
          <h1 style={{ fontSize: '20px', fontWeight: 700, marginBottom: '8px', fontFamily: 'Outfit, sans-serif' }}>
            Something went wrong
          </h1>
          <p style={{ fontSize: '14px', color: 'var(--text-muted, #6b7280)', marginBottom: '20px' }}>
            The page hit an unexpected error. Reloading usually fixes it. If it keeps happening, please let us know.
          </p>
          <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
            <button
              onClick={() => window.location.reload()}
              style={{
                padding: '10px 18px', borderRadius: '10px', border: 'none', cursor: 'pointer',
                fontSize: '14px', fontWeight: 600, color: '#fff',
                background: 'var(--brand-primary, #1B7F45)',
              }}
            >
              Reload page
            </button>
            <button
              onClick={() => { window.location.href = '/'; }}
              style={{
                padding: '10px 18px', borderRadius: '10px', cursor: 'pointer',
                fontSize: '14px', fontWeight: 600,
                background: 'transparent', color: 'var(--text-primary, #1a1a1a)',
                border: '1px solid var(--border-color, #e5e7eb)',
              }}
            >
              Go home
            </button>
          </div>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
